/** @jsxImportSource @emotion/react */
import 'twin.macro';

import { css } from '@emotion/react';
import { RefObject } from 'react';
import { Table, Row } from '@tanstack/react-table';
import { Info } from 'components/shared/Info';
import { UsageTrackingHeader } from './UsageTrackingHeader';
import { UsageTrackingRow } from './UsageTrackingRow';
import { DetailedUsage } from 'services/usage-tracking.service';
import 'components/Library/Listview.scss';

interface UsageTrackingListViewProps {
  table: Table<DetailedUsage>;
  rows: Row<DetailedUsage>[];
  tableContainerRef: RefObject<HTMLDivElement>;
  getApiKeyName: (apiName: string) => string;
  isEmpty: boolean;
}

const listViewTableStyle = css`
  ::-webkit-scrollbar {
    width: 0px;
  }
  
  /* Enable horizontal scrolling */
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  /* Show scrollbar on mobile */
  @media (max-width: 768px) {
    &::-webkit-scrollbar {
      height: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 3px;
    }
  }
`;

const tableStyle = css`
  /* Ensure table has minimum width */
  min-width: 1200px;
  table-layout: fixed;
  
  th, td {
    padding: 0.75rem 1rem;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  @media (max-width: 1024px) {
    min-width: 1000px;
    font-size: 0.9rem;
    
    th, td {
      padding: 0.625rem 0.75rem;
    }
  }
  
  @media (max-width: 768px) {
    min-width: 900px;
    font-size: 0.875rem;
    
    th, td {
      padding: 0.5rem 0.625rem;
    }
  }
  
  @media (max-width: 640px) {
    min-width: 800px;
    font-size: 0.8125rem;
    
    th, td {
      padding: 0.5rem;
    }
  }
`;

export const UsageTrackingListView = ({
  table,
  rows,
  tableContainerRef,
  getApiKeyName,
  isEmpty: isEmptyData,
}: UsageTrackingListViewProps) => {
  return (
    <div className="listViewTable" tw="block mt-2 overflow-hidden rounded-lg shadow bg-white w-full">
      <div
        ref={tableContainerRef}
        tw="max-height[400px] w-full overflow-y-auto"
        css={[listViewTableStyle]}
      >
        <table tw="w-full leading-9" css={tableStyle}>
          <thead>
            {table.getHeaderGroups().map((headerGroup, groupIndex: number) => (
              <tr key={groupIndex} className="action-item">
                {headerGroup.headers.map((header, headerIndex: number) => (
                  <UsageTrackingHeader
                    key={headerIndex}
                    header={header}
                    index={headerIndex}
                  />
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {rows.map((row, index) => (
              <UsageTrackingRow
                index={index}
                key={row.id}
                itemRow={row}
                getApiKeyName={getApiKeyName}
              />
            ))}
          </tbody>
        </table>

        {isEmptyData && (
          <div tw="ml-4 my-4">
            <Info text="Nothing to show" />
          </div>
        )}
      </div>
    </div>
  );
};